import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PropertyFilter as PropertyFilterComponent } from "@amzn/awsui-components-react";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
  PropertyFilterQuery,
  PropertyFilterToken,
} from "@amzn/awsui-collection-hooks/cjs/interfaces";
import { RootState } from "../app/rootReducer";
import {
  PropertyFilter,
  PropertyFilterGroup,
  resetActivePropertyFilters,
  setActivePropertyFilter,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
} from "../searchSideBar/searchSideBarConnector";
import {
  PROPERTY_FILTER_I18N,
  propertyFiltersConfig,
} from "../searchSideBar/searchPropertyFilter/SearchPropertyFilter-config";
import { genericStringSplitter } from "../../util/stringUtils";
import "../searchSideBar/searchPropertyFilter/SearchPropertyFilter.css";

/**
 * Rewrites the given polaris query so that any `asin` token whose
 * value contains multiple ASINs (separated by commas, whitespace, tabs
 * or newlines - matching `genericStringSplitter`) becomes an OR
 * `tokenGroup` of single-ASIN tokens.
 *
 * Applied as soon as the Cloudscape `PropertyFilter` fires its
 * `onChange`, so a user who types `MOCKASIN, AnotherMockAsin` in a
 * single ASIN filter sees the two chips split into an OR pair
 * immediately - without waiting for a Submit + URL round-trip. The
 * split shape also matches what the URL parser reproduces when the
 * shared link is opened later, so the visual state is consistent on
 * both fresh input and reload.
 *
 * Non-`asin` tokens are returned unchanged.
 *
 * Typed loosely (`any`) on purpose - the surrounding file already
 * treats polaris query shapes as untyped structural blobs because the
 * upstream `@amzn/awsui-collection-hooks` type export does not
 * consistently resolve in this workspace's TS config.
 */
function splitCommaSeparatedAsinTokens(query: any): any {
  const splitToken = (token: any): any => {
    if (token?.propertyKey !== "asin") return token;
    const raw = typeof token.value === "string" ? token.value : "";
    const parts = genericStringSplitter(raw);
    if (parts.length <= 1) return token;
    return {
      operation: "or",
      tokens: parts.map((v) => ({
        propertyKey: "asin",
        value: v,
        operator: token.operator,
      })),
    };
  };

  const walkGroup = (group: any): any => {
    if (!group || typeof group !== "object") return group;
    return {
      ...group,
      tokens: (group.tokens ?? []).map((t: any) =>
        t?.operation ? walkGroup(t) : splitToken(t)
      ),
      tokenGroups: (group.tokenGroups ?? []).map((tg: any) =>
        tg?.operation ? walkGroup(tg) : splitToken(tg)
      ),
    };
  };

  return walkGroup(query);
}

/**
 * Local shape used by the Cloudscape `PropertyFilter` primitive.
 * Extends the collection-hooks `PropertyFilterQuery` with the recursive
 * `tokenGroups` field the component supports for grouped tokens.
 */
interface PolarisPropertyFilterGroup extends PropertyFilterQuery {
  tokenGroups?: PolarisPropertyFilterGroup[];
}

export interface PropertyFilterInputProps {
  /**
   * Optional list of `propertyKey` values to hide from the Cloudscape
   * `PropertyFilter`'s property dropdown. Useful when a caller already
   * exposes a dedicated input for one of the properties (e.g. Legacy
   * `SearchPropertyFilter` owns ASIN via its dedicated textarea and
   * passes `["asin"]` here so users cannot enter conflicting ASIN
   * tokens from two places at once). Only affects what the user can
   * ADD from the UI - existing tokens whose `propertyKey` is in this
   * list are still rendered and continue to round-trip through the
   * slice.
   */
  omitProperties?: string[];
}

/**
 * Shared property-filter input.
 *
 * Renders just the Cloudscape `PropertyFilter` primitive - the horizontal
 * search input plus its chip row - and owns the two-way translation
 * between the Cloudscape "polaris" query shape and this codebase's
 * `PropertyFilterGroup` shape stored in the `generateQuery` Redux slice.
 *
 * Consumers:
 *
 * - `features/searchSideBar/searchPropertyFilter/searchPropertyFilter.tsx`
 *   (Legacy Route) wraps this component in its "More attributes"
 *   `FormField` alongside the ASIN textarea and visibility toggle, and
 *   passes `omitProperties={["asin"]}` so ASIN cannot be added from
 *   both places.
 *
 * - `features/filterBar/FilterBar.tsx` (App Shell / V2 Route) mounts
 *   this component alone so the shared filter bar shows only the search
 *   box + chips, matching the design's horizontal Filter Bar mockup.
 *   Passes no `omitProperties`, so ASIN is available as a first-class
 *   filter property in the App Shell (since the App Shell has no
 *   dedicated ASIN textarea).
 *
 * The component preserves any existing top-level `asin` PropertyFilter
 * in the slice when writing attribute filters back - so a caller (like
 * the Legacy ASIN textarea) that owns the ASIN portion is not
 * clobbered by an unrelated attribute-filter edit.
 */
export const PropertyFilterInput: React.FC<PropertyFilterInputProps> = ({
  omitProperties,
}) => {
  const dispatch = useDispatch();

  const { productLinesData } = useSelector(
    (state: RootState) => state.productLines
  );
  const { programNameData } = useSelector(
    (state: RootState) => state.upstreamAttributesData
  );
  const { queryPropertyFilters, queryPropertyFiltersForceRehydrate } =
    useSelector((state: RootState) => state.generateQuery);

  const [polarisPropertyFilter, setPolarisPropertyFilter] =
    useState<PolarisPropertyFilterGroup>({
      tokens: [],
      tokenGroups: [],
      operation: "and",
    });

  // Full config keyed by propertyKey. Used by `preProcessPolarisPropertyFilter`
  // to VALIDATE that a token's propertyKey is known - so a token whose
  // property is omitted from the UI dropdown (e.g. ASIN in Legacy) still
  // round-trips through the slice correctly when it arrives via the URL.
  const polarisPropertyFilterConfig = useMemo(
    () => propertyFiltersConfig(productLinesData, programNameData),
    [productLinesData, programNameData]
  );

  // Same config with omitted entries removed. Only used to build the
  // `filteringProperties` / `filteringOptions` arrays the Cloudscape
  // `PropertyFilter` renders in its "add filter" dropdown.
  const uiFilterableConfig = useMemo(() => {
    if (!omitProperties || omitProperties.length === 0) {
      return polarisPropertyFilterConfig;
    }
    const omit = new Set(omitProperties);
    return Object.fromEntries(
      Object.entries(polarisPropertyFilterConfig).filter(
        ([key]) => !omit.has(key)
      )
    );
  }, [polarisPropertyFilterConfig, omitProperties]);

  // Rehydrate Polaris state from the Redux `PropertyFilterGroup` on
  // first mount and whenever the slice signals a forced rehydrate
  // (URL load, preset applied, etc.).
  useEffect(() => {
    rehydratePolarisPropertyFilter(queryPropertyFilters);
  }, []);

  useEffect(() => {
    if (queryPropertyFiltersForceRehydrate) {
      rehydratePolarisPropertyFilter(queryPropertyFilters);
      dispatch(setQueryPropertyFiltersForceRehydrate(false));
    }
  }, [queryPropertyFiltersForceRehydrate]);

  const rehydratePolarisPropertyFilter = (
    propertyFilterGroup: PropertyFilterGroup
  ): void => {
    if (!propertyFilterGroup || propertyFilterGroup.filters.length === 0) {
      setPolarisPropertyFilter({ tokens: [], operation: "and" });
      return;
    }
    if (propertyFilterGroup.filters?.length > 2) {
      return; // Unrepresentable query - leave Polaris state alone
    }

    // Accumulate token groups from every top-level filter so that a
    // slice state with both a top-level `PropertyFilter(asin)` AND a
    // nested attribute `PropertyFilterGroup` renders every filter in
    // the polaris chip row - not just the last one seen.
    const merged: PolarisPropertyFilterGroup = {
      tokens: [],
      tokenGroups: [],
      operation: "and",
    };

    propertyFilterGroup.filters.forEach((property) => {
      if (property.type === "PropertyFilterGroup") {
        const next = transformPropertyFilterGroupToPolarisPropertyFilter(
          property,
          true
        );
        merged.tokenGroups = [
          ...(merged.tokenGroups ?? []),
          ...(next.tokenGroups ?? []),
        ];
        merged.tokens = [...(merged.tokens ?? []), ...(next.tokens ?? [])];
      } else if (
        property.type === "PropertyFilter" &&
        property.propertyKey === "asin"
      ) {
        // Legacy URLs (and any URL that the depth-0 special-case
        // collapsed into a single top-level `asin` PropertyFilter) can
        // arrive here with either a single ASIN value or a
        // comma-separated string. Lift them into polaris tokens so the
        // App Shell filter bar shows the ASIN filters as chips - a
        // single token when there is only one ASIN, an OR-joined
        // tokenGroup when there are several.
        const raw = typeof property.value === "string" ? property.value : "";
        const asins = raw
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
        const polarisOperator: PropertyFilterToken["operator"] =
          property.operator === "fuzzy" ? ":" : "=";
        if (asins.length === 1) {
          merged.tokenGroups!.push({
            propertyKey: "asin",
            value: asins[0],
            operator: polarisOperator,
          } as PropertyFilterToken);
        } else if (asins.length > 1) {
          merged.tokenGroups!.push({
            operation: "or",
            tokens: asins.map(
              (v) =>
                ({
                  propertyKey: "asin",
                  value: v,
                  operator: polarisOperator,
                } as PropertyFilterToken)
            ),
          } as PolarisPropertyFilterGroup);
        }
      }
    });

    setPolarisPropertyFilter(preProcessPolarisPropertyFilter(merged));
  };

  // Write attribute filters back to Redux while preserving any existing
  // `asin` PropertyFilter contributed by an out-of-band source (e.g. the
  // Legacy ASIN textarea).
  const updateQueryFromPropertyFilters = (
    query: PropertyFilterProps.Query
  ): void => {
    const preserved = queryPropertyFilters?.filters?.filter(
      (f: PropertyFilter | PropertyFilterGroup) =>
        f.type === "PropertyFilter" && f.propertyKey === "asin"
    );
    const filters = [...(preserved ?? [])];
    const attributeFilters =
      transformPolarisPropertyFilterToPropertyFilterGroup(query);
    if (attributeFilters?.filters.length > 0) {
      filters.push(attributeFilters);
    }
    dispatch(
      setQueryPropertyFilters({
        type: "PropertyFilterGroup",
        filters,
        operation: "and",
      })
    );
  };

  // Filter out unknown properties and empty values; refresh the active
  // filter set used elsewhere (e.g. visibility indicators).
  const preProcessPolarisPropertyFilter = (
    query: any,
    root = true
  ): PolarisPropertyFilterGroup => {
    if (root) {
      dispatch(resetActivePropertyFilters());
    }
    const subQuery: any = {
      tokens: [],
      tokenGroups: [],
      operation: query.operation ?? "and",
    };
    if (query?.operation) {
      const tokens = root ? query?.tokenGroups : query?.tokens;
      (tokens ?? []).forEach((token: any) => {
        if (token?.operation) {
          const nested = preProcessPolarisPropertyFilter(token, false);
          if (nested?.tokens.length > 0) {
            if (root) subQuery.tokenGroups.push(nested);
            else subQuery.tokens.push(nested);
          }
        } else if (
          token?.propertyKey &&
          polarisPropertyFilterConfig[token?.propertyKey]
        ) {
          if (token?.value && token?.value?.toString()?.trim() !== "") {
            if (root) subQuery.tokenGroups.push(token);
            else subQuery.tokens.push(token);
            dispatch(setActivePropertyFilter(token?.propertyKey));
          }
        }
      });
    }
    return subQuery;
  };

  const transformPolarisPropertyFilterToPropertyFilterGroup = (
    group: PolarisPropertyFilterGroup
  ): PropertyFilterGroup => {
    const propertyFilterGroup: PropertyFilterGroup = {
      type: "PropertyFilterGroup",
      filters: [],
      operation: group.operation,
    };
    [...(group?.tokens ?? []), ...(group?.tokenGroups ?? [])].forEach(
      (token: PolarisPropertyFilterGroup | PropertyFilterToken) => {
        if ("operation" in token) {
          propertyFilterGroup.filters.push(
            transformPolarisPropertyFilterToPropertyFilterGroup(token)
          );
        } else if ("propertyKey" in token) {
          propertyFilterGroup.filters.push(
            transformPolarisPropertyFilterTokenToPropertyFilter(token)
          );
        }
      }
    );
    return propertyFilterGroup;
  };

  const transformPropertyFilterGroupToPolarisPropertyFilter = (
    propertyFilterGroup: PropertyFilterGroup,
    root = false
  ): PolarisPropertyFilterGroup => {
    const group: any = {
      tokens: [],
      tokenGroups: [],
      operation: propertyFilterGroup.operation,
    };
    (propertyFilterGroup?.filters ?? []).forEach(
      (property: PropertyFilter | PropertyFilterGroup) => {
        if (property.type === "PropertyFilterGroup") {
          if (root) {
            group.tokenGroups.push(
              transformPropertyFilterGroupToPolarisPropertyFilter(
                property,
                false
              )
            );
          } else {
            group.tokens.push(
              transformPropertyFilterGroupToPolarisPropertyFilter(
                property,
                false
              )
            );
          }
        } else if (property.type === "PropertyFilter") {
          if (root) {
            group.tokenGroups.push(
              transformPropertyFilterToPolarisPropertyFilterToken(property)
            );
          } else {
            group.tokens.push(
              transformPropertyFilterToPolarisPropertyFilterToken(property)
            );
          }
        }
      }
    );
    return group;
  };

  const transformPolarisPropertyFilterTokenToPropertyFilter = (
    token: PropertyFilterToken
  ): PropertyFilter => ({
    type: "PropertyFilter",
    propertyKey: token?.propertyKey ?? "",
    value: token?.value ?? "",
    operator: token?.operator === ":" ? "fuzzy" : "filter",
  });

  const transformPropertyFilterToPolarisPropertyFilterToken = (
    property: PropertyFilter
  ): PropertyFilterToken => ({
    propertyKey: property?.propertyKey,
    value: property?.value ?? "",
    operator: property?.operator === "fuzzy" ? ":" : "=",
  });

  return (
    <PropertyFilterComponent
      query={polarisPropertyFilter}
      onChange={({ detail }) => {
        // Split multi-value ASIN tokens into OR-joined chip groups so
        // "X, Y" typed into a single ASIN filter renders as two chips
        // joined by OR immediately, matching the shape the URL parser
        // reproduces when the shared link is later opened.
        const split = splitCommaSeparatedAsinTokens({
          ...detail,
        } as PolarisPropertyFilterGroup);
        const next = preProcessPolarisPropertyFilter(split);
        setPolarisPropertyFilter(next);
        updateQueryFromPropertyFilters(next);
      }}
      expandToViewport
      i18nStrings={PROPERTY_FILTER_I18N}
      enableTokenGroups
      customGroupsText={[]}
      freeTextFiltering={{ operators: ["="], defaultOperator: "=" }}
      disableFreeTextFiltering={true}
      virtualScroll={true}
      filteringAriaLabel="Filter based on attribute values"
      filteringPlaceholder="Filter based on attribute values"
      filteringOptions={
        Object.entries(uiFilterableConfig)?.flatMap(
          ([_, property]) => property?.filteringOptions ?? []
        ) ?? []
      }
      filteringProperties={
        Object.entries(uiFilterableConfig)?.map(
          ([_, property]) => property.filteringProperty
        ) ?? []
      }
    />
  );
};

export default PropertyFilterInput;
